import React, { useEffect, useMemo, useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Box, Button, Typography, TextField, InputAdornment, Chip, Card, CardContent,
  Grid, IconButton, Avatar, Collapse, Dialog, DialogTitle, DialogContent, useMediaQuery, useTheme, TablePagination
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import SummaryCard from './SummaryCard';
import RefreshIcon from '@mui/icons-material/Refresh';
import BuildIcon from '@mui/icons-material/Build';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import HistoryIcon from '@mui/icons-material/History';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import api, { partService } from '../api/api';
import AddPartDialog from './AddPartDialog';
import EditPartDialog from './EditPartDialog';
import InvoiceAttachmentList from './InvoiceAttachmentList';
import { getVehicleCategoryId } from '../utils/vehicleCategories';
import { formatPortalDate } from '../utils/dateFormat';
import { currentPartPlacement } from '../utils/partLocation.mjs';


function getMovementChipSx(type) {
  const t = String(type || '').toLowerCase();
  if (t === 'removed') return { backgroundColor: '#ffebee', color: '#c62828' };
  if (t === 'assigned') return { backgroundColor: '#e3f2fd', color: '#1565c0' };
  if (t === 'unassigned') return { backgroundColor: '#eeeeee', color: '#424242' };
  if (t === 'installed') return { backgroundColor: '#e8f5e9', color: '#2e7d32' };
  if (t === 'transferred') return { backgroundColor: '#fff3e0', color: '#e65100' };
  return { backgroundColor: '#eeeeee', color: '#424242' };
}

function isPartsTruck(vehicle) {
  return Boolean(vehicle && getVehicleCategoryId(vehicle) === 'partsTruck');
}

function inventoryStorageLocation(part) {
  const { storageArea } = currentPartPlacement(part);
  return { Shelf: 'shelf', 'Solvent Shed': 'solventShed', Shop: 'shop' }[storageArea] || null;
}

const inventoryRowPalettes = {
  shelf: {
    label: 'Inventory: Shelf', rowBg: '#f8fcf9', hoverBg: '#f1faf3',
    chipBg: '#eef9f1', chipColor: '#287a43', borderColor: '#bbf7d0',
  },
  partsTruck: {
    rowBg: '#f8fbff', hoverBg: '#eef6ff',
    chipBg: '#edf6ff', chipColor: '#236a9b', borderColor: '#bfdbfe',
  },
  solventShed: {
    label: 'Inventory: Solvent Shed', rowBg: '#fcfaff', hoverBg: '#f7f2ff',
    chipBg: '#f5efff', chipColor: '#76509a', borderColor: '#e9d5ff',
  },
  shop: {
    label: 'Inventory: Shop', rowBg: '#fffdf8', hoverBg: '#fff8eb',
    chipBg: '#fff5e5', chipColor: '#9a5b26', borderColor: '#fed7aa',
  },
};

function getPartInventoryInfo(part, truckById) {
  const intendedVehicle = part.unit_intended_for ? truckById.get(Number(part.unit_intended_for)) : null;
  const cameOffVehicle = part.unit_came_off_from ? truckById.get(Number(part.unit_came_off_from)) : null;
  const storageLocation = inventoryStorageLocation(part);

  if (storageLocation) {
    return {
      inInventory: true,
      type: storageLocation,
      ...inventoryRowPalettes[storageLocation],
    };
  }

  if (!part.unit_intended_for && isPartsTruck(cameOffVehicle)) {
    return {
      inInventory: true,
      type: 'partsTruck',
      label: `Inventory: ${cameOffVehicle.name || 'Parts Truck'}`,
      ...inventoryRowPalettes.partsTruck,
    };
  }

  if (isPartsTruck(intendedVehicle)) {
    return {
      inInventory: true,
      type: 'partsTruck',
      label: `Inventory: ${intendedVehicle.name || 'Parts Truck'}`,
      ...inventoryRowPalettes.partsTruck,
    };
  }

  if (!currentPartPlacement(part).truckId) {
    return {
      inInventory: true,
      type: 'shelf',
      ...inventoryRowPalettes.shelf,
    };
  }

  return {
    inInventory: false,
    type: 'vehicle',
    label: 'Assigned Vehicle',
    rowBg: 'inherit',
    hoverBg: '#f5f5f5',
    chipBg: '#f1f5f9',
    chipColor: '#475569',
    borderColor: 'transparent',
  };
}

function PartRow({ part, onEdit, onDelete, getTruckName, truckById }) {
  const theme = useTheme();
  const photoDialogFullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = useState(false);
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [photoZoom, setPhotoZoom] = useState(1);
  const [photoDialogIndex, setPhotoDialogIndex] = useState(0);
  const [partPhotos, setPartPhotos] = useState([]);
  const [movements, setMovements] = useState([]);
  const [loadingMovements, setLoadingMovements] = useState(false);

  const resolvedPartPhotos = useMemo(() => {
    const gallery = (Array.isArray(partPhotos) ? partPhotos : []).filter((photo) => photo?.url);
    const cover = part.photo_url ? { id: 'cover', url: part.photo_url, original_name: 'Cover photo' } : null;
    if (!cover) return gallery;
    return gallery.some((photo) => photo.url === cover.url) ? gallery : [cover, ...gallery];
  }, [part.photo_url, partPhotos]);

  const selectedPhotoUrl = resolvedPartPhotos[photoDialogIndex]?.url || null;
  const quantity = Number(part.quantity ?? 0);
  const lowStockThreshold = Number(part.low_stock_threshold ?? 2);
  const isLowStock = quantity < lowStockThreshold;
  const inventoryInfo = getPartInventoryInfo(part, truckById);

  const openPhotoDialog = (index = 0) => {
    setPhotoDialogIndex(index);
    setPhotoZoom(1);
    setPhotoDialogOpen(true);
  };

  const getConditionColor = (scale) => {
    if (scale >= 4) return '#4caf50';
    if (scale >= 3) return '#ff9800';
    return '#f44336';
  };

  const getConditionLabel = (scale) => {
    if (scale >= 4) return 'Excellent';
    if (scale >= 3) return 'Good';
    return 'Fair';
  };

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    async function loadHistory() {
      try {
        setLoadingMovements(true);
        const res = await fetch(`/api/parts/${part.id}/history`);
        const data = await res.json();
        if (!cancelled) setMovements(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching part movement history:', err);
        if (!cancelled) setMovements([]);
      } finally {
        if (!cancelled) setLoadingMovements(false);
      }
    }

    async function loadPhotos() {
      try {
        const res = await partService.getPhotos(part.id);
        if (!cancelled) setPartPhotos(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error('Error fetching part photos:', err);
        if (!cancelled) setPartPhotos([]);
      }
    }

    const refreshOpenRow = () => {
      loadHistory();
      loadPhotos();
    };

    refreshOpenRow();
    window.addEventListener('dataUpdated', refreshOpenRow);
    window.addEventListener('partsUpdated', refreshOpenRow);
    return () => {
      cancelled = true;
      window.removeEventListener('dataUpdated', refreshOpenRow);
      window.removeEventListener('partsUpdated', refreshOpenRow);
    };
  }, [open, part.id, part.photo_url]);

  return (
    <>
      <TableRow
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.target !== event.currentTarget) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setOpen((current) => !current);
          }
        }}
        sx={{
          '& > *': { borderBottom: 'unset' },
          backgroundColor: inventoryInfo.rowBg,
          borderLeft: `6px solid ${inventoryInfo.borderColor}`,
          '&:hover': { backgroundColor: inventoryInfo.hoverBg },
          cursor: 'pointer',
          transition: 'background-color 0.2s ease',
        }}
      >
        <TableCell>
          <IconButton
            size="small"
            aria-label={open ? 'Hide part details' : 'Show part details'}
            onClick={(event) => {
              event.stopPropagation();
              setOpen((current) => !current);
            }}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#001f3f' }}>
            {part.sku}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#001f3f' }}>
            {part.part_type}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {part.make} {part.model}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontWeight: 700, color: part.part_number_oem ? '#001f3f' : '#9e9e9e' }}>
            {part.part_number_oem || '—'}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontWeight: 700, color: part.serial_number ? '#001f3f' : '#9e9e9e' }}>
            {part.serial_number || '—'}
          </Typography>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, alignItems: 'flex-start' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <LocationOnIcon sx={{ fontSize: 18, color: inventoryInfo.inInventory ? inventoryInfo.borderColor : '#1976d2' }} />
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {part.location}
              </Typography>
            </Box>
            {inventoryInfo.inInventory && (
              <Chip
                size="small"
                icon={<Inventory2Icon sx={{ fontSize: '16px !important' }} />}
                label={inventoryInfo.label}
                sx={{
                  backgroundColor: inventoryInfo.chipBg,
                  color: inventoryInfo.chipColor,
                  border: `1px solid ${inventoryInfo.borderColor}55`,
                  fontWeight: 900,
                  '& .MuiChip-icon': { color: inventoryInfo.chipColor },
                }}
              />
            )}
          </Box>
        </TableCell>
        <TableCell align="center">
          <Chip
            label={part.quantity ?? 0}
            size="small"
            sx={{
              backgroundColor: isLowStock ? '#ffebee' : '#e3f2fd',
              color: isLowStock ? '#c62828' : '#1565c0',
              border: isLowStock ? '1px solid #ef9a9a' : 'none',
              fontWeight: 'bold',
              minWidth: 48
            }}
          />
        </TableCell>
        <TableCell align="center">
          <Chip
            label={`${part.condition_scale}/5`}
            size="small"
            sx={{
              backgroundColor: getConditionColor(part.condition_scale),
              color: 'white',
              fontWeight: 'bold',
              minWidth: 60
            }}
          />
          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
            {getConditionLabel(part.condition_scale)}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Chip
            label={part.used_new ? 'New' : 'Used'}
            size="small"
            sx={{
              backgroundColor: part.used_new ? '#c8e6c9' : '#ffccbc',
              color: part.used_new ? '#2e7d32' : '#d84315',
              fontWeight: 'bold'
            }}
          />
        </TableCell>
        <TableCell align="right">
          {part.current_value ? (
            <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
              ${parseFloat(part.current_value).toFixed(2)}
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ color: '#9e9e9e', fontStyle: 'italic' }}>
              —
            </Typography>
          )}
        </TableCell>
        <TableCell align="center">
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onEdit(part); }}
              sx={{ color: '#1976d2', '&:hover': { backgroundColor: '#e3f2fd' } }}
            >
              <EditIcon fontSize="small" />
            </IconButton>

            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(part.id);
              }}
              sx={{ color: '#d32f2f', '&:hover': { backgroundColor: '#ffebee' } }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </TableCell>
      </TableRow>
      <TableRow sx={{ backgroundColor: inventoryInfo.inInventory ? inventoryInfo.rowBg : 'inherit' }}>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={11}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 2, px: 3, backgroundColor: inventoryInfo.inInventory ? inventoryInfo.rowBg : '#f8f9fa' }}>
              <Grid container spacing={2}>
                {(resolvedPartPhotos.length || part.invoice_url) ? (
                  <>
                    <Grid item xs={12} md={3}>
                      <Grid container spacing={2}>
                        {resolvedPartPhotos.length > 0 && (
                          <Grid item xs={12}>
                            <Paper sx={{
                              p: 1.25,
                              backgroundColor: 'white',
                              border: '2px solid #2e7d32',
                              borderRadius: 2
                            }}>
                              <Typography variant="subtitle2" sx={{
                                fontWeight: 'bold',
                                mb: 1,
                                color: '#2e7d32',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                              }}>
                                <Inventory2Icon /> Part Photos ({resolvedPartPhotos.length})
                              </Typography>
                              <Box sx={{
                                textAlign: 'center',
                                backgroundColor: '#f5f5f5',
                                borderRadius: 2,
                                p: 1,
                                minHeight: 110,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1,
                                flexWrap: 'wrap'
                              }}>
                                {resolvedPartPhotos.map((photo, index) => (
                                  <Box
                                    key={`${photo.id || photo.url}-${index}`}
                                    component="img"
                                    src={photo.url}
                                    alt={`Part ${index + 1}`}
                                    sx={{
                                      width: resolvedPartPhotos.length === 1 ? '100%' : 86,
                                      maxWidth: '100%',
                                      height: resolvedPartPhotos.length === 1 ? 120 : 72,
                                      objectFit: 'contain',
                                      backgroundColor: 'white',
                                      borderRadius: 1.5,
                                      border: '1px solid #d7e3f4',
                                      boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                                      cursor: 'zoom-in'
                                    }}
                                    onClick={() => openPhotoDialog(index)}
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none';
                                    }}
                                  />
                                ))}
                              </Box>
                            </Paper>
                          </Grid>
                        )}

                        {part.invoice_url && (
                          <Grid item xs={12}>
                            <Paper sx={{
                              p: 2,
                              backgroundColor: 'white',
                              border: '2px solid #1976d2',
                              borderRadius: 2
                            }}>
                              <InvoiceAttachmentList value={part.invoice_url} title="Invoice Attachments" />
                            </Paper>
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                    <Grid item xs={12} md={9}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Paper sx={{ p: 2, backgroundColor: 'white', height: '100%' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#001f3f' }}>
                              Part Details
                            </Typography>
                            <Typography variant="caption" color="textSecondary">OEM Number</Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>{part.part_number_oem || '—'}</Typography>
                            <Typography variant="caption" color="textSecondary">Serial Number</Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>{part.serial_number || '—'}</Typography>
                            <Typography variant="caption" color="textSecondary">Cross Reference</Typography>
                            <Typography variant="body2">{part.cross_reference || '—'}</Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Paper sx={{ p: 2, backgroundColor: 'white', height: '100%' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#001f3f' }}>
                              Storage
                            </Typography>
                            <Typography variant="caption" color="textSecondary">Current Storage</Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>{part.condition_of_storage || '—'}</Typography>
                            <Typography variant="caption" color="textSecondary">Recommended</Typography>
                            <Typography variant="body2">{part.recommended_storage || '—'}</Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={12}>
                          <Paper sx={{ p: 2, backgroundColor: 'white', height: '100%' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#001f3f' }}>
                              Financial
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                              <Box>
                                <Typography variant="caption" color="textSecondary">Purchase Price</Typography>
                                <Typography variant="body2">{part.purchase_price ? `$${part.purchase_price}` : '—'}</Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" color="textSecondary">Current Value</Typography>
                                <Typography variant="body2">{part.current_value ? `$${part.current_value}` : '—'}</Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" color="textSecondary">Receipt</Typography>
                                <Typography variant="body2">{part.receipt_number || '—'}</Typography>
                              </Box>
                            </Box>
                          </Paper>
                        </Grid>
                      </Grid>
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid item xs={12} sm={6} md={4}>
                      <Paper sx={{ p: 2, backgroundColor: 'white', height: '100%' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#001f3f' }}>
                          Part Details
                        </Typography>
                        <Typography variant="caption" color="textSecondary">OEM Number</Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>{part.part_number_oem || '—'}</Typography>
                        <Typography variant="caption" color="textSecondary">Serial Number</Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>{part.serial_number || '—'}</Typography>
                        <Typography variant="caption" color="textSecondary">Cross Reference</Typography>
                        <Typography variant="body2">{part.cross_reference || '—'}</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Paper sx={{ p: 2, backgroundColor: 'white', height: '100%' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#001f3f' }}>
                          Storage
                        </Typography>
                        <Typography variant="caption" color="textSecondary">Current Storage</Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>{part.condition_of_storage || '—'}</Typography>
                        <Typography variant="caption" color="textSecondary">Recommended</Typography>
                        <Typography variant="body2">{part.recommended_storage || '—'}</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Paper sx={{ p: 2, backgroundColor: 'white', height: '100%' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#001f3f' }}>
                          Financial
                        </Typography>
                        <Typography variant="caption" color="textSecondary">Purchase Price</Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>{part.purchase_price ? `$${part.purchase_price}` : '—'}</Typography>
                        <Typography variant="caption" color="textSecondary">Receipt</Typography>
                        <Typography variant="body2">{part.receipt_number || '—'}</Typography>
                      </Paper>
                    </Grid>
                  </>
                )}

                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, backgroundColor: '#f8fbff', border: '2px solid #d7e8ff', borderRadius: 2, height: '100%' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#0d47a1' }}>
                      Part Notes
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: part.notes ? 'text.primary' : 'text.secondary' }}>
                      {part.notes || 'No part notes added.'}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, backgroundColor: '#fff8e1', border: '2px solid #ffe082', borderRadius: 2, height: '100%' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#8d6e00' }}>
                      Condition Notes
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: part.condition_notes ? 'text.primary' : 'text.secondary' }}>
                      {part.condition_notes || 'No condition notes added.'}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <Paper sx={{ p: 2, backgroundColor: 'white', border: '2px solid #d1d9ff', borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{
                      fontWeight: 'bold',
                      mb: 2,
                      color: '#1a237e',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}>
                      <HistoryIcon /> Inventory Transfer History
                    </Typography>

                    {loadingMovements ? (
                      <Typography variant="body2">Loading movement history...</Typography>
                    ) : movements.length === 0 ? (
                      <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#777' }}>
                        No movement history available
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {movements.map((m) => (
                          <Box
                            key={m.id}
                            sx={{
                              p: 1.5,
                              borderRadius: 2,
                              backgroundColor: '#fafcff',
                              border: '1px solid #e3e8ff',
                              display: 'flex',
                              gap: 1.5,
                              flexDirection: { xs: 'column', sm: 'row' },
                              alignItems: { xs: 'flex-start', sm: 'center' },
                              justifyContent: 'space-between'
                            }}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Chip
                                  size="small"
                                  label={(m.movement_type || 'movement').toString().toUpperCase()}
                                  sx={{ ...getMovementChipSx(m.movement_type), fontWeight: 'bold' }}
                                />
                                <Typography variant="caption" color="textSecondary">
                                  {m.from_location || getTruckName?.(m.from_truck_id, 'Shelf')} → {m.to_location || getTruckName?.(m.to_truck_id)}
                                </Typography>
                              </Box>
                              {m.notes && (
                                <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                                  {m.notes}
                                </Typography>
                              )}
                            </Box>
                            <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                              <Typography variant="caption" display="block" color="textSecondary">
                                {m.created_at ? formatPortalDate(m.created_at, { includeTime: true }) : ''}
                              </Typography>
                              {m.moved_by && (
                                <Typography variant="caption" display="block" color="textSecondary">
                                  By: {m.moved_by}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      <Dialog
        open={photoDialogOpen}
        onClose={() => setPhotoDialogOpen(false)}
        fullScreen={photoDialogFullScreen}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: photoDialogFullScreen ? 0 : 3 } }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'linear-gradient(135deg, #001f3f 0%, #003d7a 100%)',
          color: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Inventory2Icon />
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {part.sku} — Photo
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {selectedPhotoUrl && (
              <IconButton
                color="inherit"
                onClick={() => window.open(selectedPhotoUrl, '_blank')}
                sx={{ backgroundColor: 'rgba(255,255,255,0.08)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.16)' } }}
                size="small"
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton
              color="inherit"
              onClick={() => setPhotoZoom((z) => Math.min(4, Number((z + 0.25).toFixed(2))))}
              sx={{ backgroundColor: 'rgba(255,255,255,0.08)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.16)' } }}
              size="small"
              aria-label="Zoom in"
            >
              <ZoomInIcon fontSize="small" />
            </IconButton>
            <IconButton
              color="inherit"
              onClick={() => setPhotoZoom((z) => Math.max(1, Number((z - 0.25).toFixed(2))))}
              sx={{ backgroundColor: 'rgba(255,255,255,0.08)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.16)' } }}
              size="small"
              aria-label="Zoom out"
            >
              <ZoomOutIcon fontSize="small" />
            </IconButton>
            <IconButton
              color="inherit"
              onClick={() => setPhotoZoom(1)}
              sx={{ backgroundColor: 'rgba(255,255,255,0.08)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.16)' } }}
              size="small"
              aria-label="Reset zoom"
            >
              <RestartAltIcon fontSize="small" />
            </IconButton>
            <IconButton onClick={() => setPhotoDialogOpen(false)} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, backgroundColor: '#0b1b2a' }}>
          <Box
            sx={{
              width: '100%',
              height: photoDialogFullScreen ? 'calc(100vh - 64px)' : '70vh',
              overflow: 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2
            }}
          >
            {selectedPhotoUrl ? (
              <Box
                component="img"
                src={selectedPhotoUrl}
                alt="Part full"
                sx={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  transform: `scale(${photoZoom})`,
                  transformOrigin: 'center center',
                  transition: 'transform 120ms ease-out',
                  cursor: photoZoom > 1 ? 'move' : 'zoom-in'
                }}
                onClick={() => setPhotoZoom((z) => (z === 1 ? 1.5 : 1))}
              />
            ) : (
              <Box sx={{ p: 3, color: 'white' }}>
                <Typography>No photo available.</Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function PartsTable() {
  const [rows, setRows] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [partTypeFilter, setPartTypeFilter] = useState('all');
  const [inventoryLocationFilter, setInventoryLocationFilter] = useState('all');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedPart, setSelectedPart] = useState(null);

  useEffect(() => {
    fetchRows();
  }, []);

  useEffect(() => {
    const handler = () => fetchRows();
    window.addEventListener('dataUpdated', handler);
    return () => window.removeEventListener('dataUpdated', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePartAdded = async (newPart) => {
    setRows(prev => [...prev.filter((part) => part.id !== newPart.id), newPart]);
    await fetchRows();
    window.dispatchEvent(new Event('dataUpdated'));
    window.dispatchEvent(new Event('partsUpdated'));
  };

  const handleEditPart = (part) => {
    setSelectedPart(part);
    setEditDialogOpen(true);
  };

  const handlePartUpdated = async (updatedPart) => {
    setRows(prev => prev.map(p => p.id === updatedPart.id ? updatedPart : p));
    await fetchRows();
    window.dispatchEvent(new Event('dataUpdated'));
    window.dispatchEvent(new Event('partsUpdated'));
  };

  const handleExportParts = async () => {
    try {
      const res = await api.get('/export/parts', { responseType: 'blob', timeout: 120000 });
      const disposition = res.headers?.['content-disposition'] || '';
      const match = String(disposition).match(/filename="?([^";]+)"?/i);
      const filename = match?.[1] || 'biofleet_parts_export.zip';
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting parts zip:', err);
      alert(err?.response?.data?.error || err?.message || 'Failed to export parts.');
    }
  };

  const handleDeletePart = async (id) => {
    const confirmDelete = window.confirm("Archive this part? It will disappear from active inventory but remain recoverable in the database.");
    if (!confirmDelete) return;

    try {
      await partService.delete(id);

      // Remove from UI
      setRows(prev => prev.filter(p => p.id !== id));
      await fetchRows();
      window.dispatchEvent(new Event('dataUpdated'));
      window.dispatchEvent(new Event('partsUpdated'));

    } catch (err) {
      console.error("Error deleting part:", err);
      alert(err?.response?.data?.error || err?.message || "Failed to delete part.");
    }
  };

  async function fetchRows() {
    try {
      const [partsRes, trucksRes] = await Promise.all([
        api.get('/parts'),
        api.get('/trucks'),
      ]);
      setRows(partsRes.data || []);
      setTrucks(trucksRes.data || []);
    } catch (err) {
      console.error(err);
    }
  }

  const truckNameById = useMemo(() => {
    const map = new Map();
    (trucks || []).forEach((t) => map.set(t.id, t.name || `${t.make || ''} ${t.model || ''}`.trim() || `Truck ${t.id}`));
    return map;
  }, [trucks]);

  const truckById = useMemo(() => {
    const map = new Map();
    (trucks || []).forEach((truck) => map.set(Number(truck.id), truck));
    return map;
  }, [trucks]);

  const getTruckName = (truckId, fallback = 'Unassigned') => {
    if (!truckId) return fallback;
    return truckNameById.get(truckId) || `Truck #${truckId}`;
  };

  const typeFilteredRows = useMemo(() => {
    if (partTypeFilter === 'new') {
      return rows.filter((row) => row.used_new === true);
    }

    if (partTypeFilter === 'used') {
      return rows.filter((row) => row.used_new === false);
    }

    return rows;
  }, [rows, partTypeFilter]);

  const inventoryFilteredRows = useMemo(() => {
    if (inventoryLocationFilter === 'all') return typeFilteredRows;
    return typeFilteredRows.filter((row) => getPartInventoryInfo(row, truckById).type === inventoryLocationFilter);
  }, [typeFilteredRows, inventoryLocationFilter, truckById]);

  const filteredRows = useMemo(() => {
    const q = searchTerm.toLowerCase();
    if (!q) {
      return inventoryFilteredRows;
    }

    return inventoryFilteredRows.filter((row) =>
      row.sku?.toLowerCase().includes(q) ||
      row.part_type?.toLowerCase().includes(q) ||
      row.location?.toLowerCase().includes(q) ||
      row.part_number_oem?.toLowerCase().includes(q) ||
      row.serial_number?.toLowerCase().includes(q)
    );
  }, [inventoryFilteredRows, searchTerm]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const paginatedRows = useMemo(
    () => filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredRows, page, rowsPerPage],
  );

  useEffect(() => {
    setPage(0);
  }, [searchTerm, partTypeFilter, inventoryLocationFilter]);

  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(filteredRows.length / rowsPerPage) - 1);
    if (page > lastPage) setPage(lastPage);
  }, [filteredRows.length, page, rowsPerPage]);

  const totalValue = rows.reduce((sum, p) => sum + (parseFloat(p.current_value) || 0), 0);
  const newParts = rows.filter(p => p.used_new === true);
  const usedParts = rows.filter(p => p.used_new === false);

  return (
    <Box sx={{ p: { xs: 1.25, sm: 1.5, md: 2 }, minHeight: '100dvh' }}>
      <Card className="portal-page-header" sx={{
        mb: 1.25,
        background: 'linear-gradient(135deg, #001f3f 0%, #003d7a 100%)',
        color: 'white',
        borderRadius: 2.25,
        boxShadow: '0 6px 24px rgba(0, 31, 63, 0.26)'
      }}>
        <CardContent sx={{ p: { xs: 1.15, sm: 1.35, md: 1.55 }, '&:last-child': { pb: { xs: 1.15, sm: 1.35, md: 1.55 } } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.25 }, flexWrap: 'wrap' }}>
            <Avatar sx={{
              width: { xs: 40, sm: 46 },
              height: { xs: 40, sm: 46 },
              backgroundColor: '#00ff97',
              boxShadow: '0 3px 14px rgba(0, 255, 151, 0.35)'
            }}>
              <Inventory2Icon sx={{ fontSize: { xs: 23, sm: 27 }, color: '#001f3f' }} />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 'auto' } }}>
              <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 0.1, fontSize: { xs: '1rem', sm: '1.18rem', md: '1.3rem' }, lineHeight: 1.05 }}>
                Vehicle Parts
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, fontSize: { xs: '0.68rem', sm: '0.76rem' }, lineHeight: 1.25 }}>
                Comprehensive Parts Management System
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAddDialogOpen(true)}
                sx={{
                  backgroundColor: '#00ff97',
                  color: '#001f3f',
                  fontWeight: 'bold',
                  px: 1.55,
                  py: 0.62,
                  minHeight: 36,
                  borderRadius: 1.6,
                  fontSize: { xs: '0.78rem', sm: '0.86rem' },
                  '&:hover': {
                    backgroundColor: '#00cc7a',
                    boxShadow: '0 4px 20px rgba(0, 255, 151, 0.4)'
                  }
                }}
              >
                Add Part
              </Button>
              <Button
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                onClick={handleExportParts}
                sx={{
                  borderColor: '#00ff97',
                  color: 'white',
                  fontWeight: 'bold',
                  px: 1.55,
                  py: 0.62,
                  minHeight: 36,
                  borderRadius: 1.6,
                  fontSize: { xs: '0.78rem', sm: '0.86rem' },
                  '&:hover': {
                    borderColor: '#00cc7a',
                    backgroundColor: 'rgba(0, 255, 151, 0.1)'
                  }
                }}
              >
                Export Parts
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={1} sx={{ mb: 1.75 }}>
        {[
          { label: 'Total Parts', value: rows.length, accent: '#3b658b', tint: '#c6dff7', icon: <BuildIcon />, filter: 'all' },
          { label: 'Total Value', value: `$${totalValue.toFixed(2)}`, accent: '#356d59', tint: '#c4e8d1', icon: <AttachMoneyIcon />, filter: 'all', unselected: true },
          { label: 'New Parts', value: newParts.length, accent: '#866523', tint: '#f9dfa4', icon: 'NEW', filter: 'new' },
          { label: 'Used Parts', value: usedParts.length, accent: '#775e8b', tint: '#e4cef1', icon: 'USED', filter: 'used' },
        ].map((item) => (
          <Grid size={{ xs: 6, md: 3 }} key={item.label}>
            <SummaryCard {...item} selected={!item.unselected && partTypeFilter === item.filter} onClick={() => setPartTypeFilter(item.filter)} />
          </Grid>
        ))}
      </Grid>

      <Card sx={{ borderRadius: 3, boxShadow: 2, mb: 1.75 }}>
        <CardContent sx={{ p: { xs: 1.25, sm: 1.5 }, '&:last-child': { pb: { xs: 1.25, sm: 1.5 } } }}>
          <Box sx={{ display: 'flex', gap: 1.25, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' } }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by SKU, type, location, OEM, or serial number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#1976d2' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#1976d2',
                  },
                },
              }}
            />
            <Button
              variant="contained"
              onClick={fetchRows}
              startIcon={<RefreshIcon />}
              sx={{
                minWidth: { xs: '100%', sm: 140 },
                height: 42,
                background: 'linear-gradient(135deg, #001f3f 0%, #003d7a 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #003d7a 0%, #001f3f 100%)',
                }
              }}
            >
              Refresh
            </Button>
          </Box>
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="textSecondary">
              Showing {filteredRows.length} of {rows.length} parts
            </Typography>
            {partTypeFilter !== 'all' && (
              <Chip
                size="small"
                label={`Type: ${partTypeFilter === 'new' ? 'New Parts' : 'Used Parts'}`}
                onDelete={() => setPartTypeFilter('all')}
                sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 'bold' }}
              />
            )}
          </Box>
        </CardContent>
      </Card>

      <Paper sx={{
        p: 1.5,
        mb: 2,
        borderRadius: 2,
        display: 'flex',
        gap: 1,
        alignItems: 'center',
        flexWrap: 'wrap',
        border: '1px solid #d8e2ef'
      }}>
        <Typography variant="body2" sx={{ fontWeight: 900, color: '#001f3f', mr: 0.5 }}>
          Filter by inventory location:
        </Typography>
        {[
          { type: 'shelf', label: 'Shelf stock' },
          { type: 'partsTruck', label: 'Parts Truck stock' },
          { type: 'solventShed', label: 'Solvent Shed stock' },
          { type: 'shop', label: 'Shop stock' },
        ].map((option) => {
          const palette = inventoryRowPalettes[option.type];
          const active = inventoryLocationFilter === option.type;
          const count = rows.filter((row) => getPartInventoryInfo(row, truckById).type === option.type).length;
          return (
            <Button
              key={option.type}
              size="small"
              variant="outlined"
              startIcon={<Inventory2Icon sx={{ fontSize: '16px !important' }} />}
              onClick={() => setInventoryLocationFilter(active ? 'all' : option.type)}
              aria-pressed={active}
              sx={{
                minHeight: 32,
                borderRadius: 999,
                px: 1.35,
                textTransform: 'none',
                bgcolor: active ? palette.hoverBg : palette.chipBg,
                color: palette.chipColor,
                border: `2px solid ${active ? palette.borderColor : `${palette.borderColor}66`}`,
                boxShadow: active ? `0 0 0 3px ${palette.borderColor}22` : 'none',
                fontWeight: 900,
                '&:hover': { bgcolor: palette.hoverBg, borderColor: palette.borderColor },
                '& .MuiButton-startIcon': { color: palette.chipColor },
              }}
            >
              {option.label} ({count})
            </Button>
          );
        })}
        {inventoryLocationFilter !== 'all' && (
          <Button size="small" color="inherit" onClick={() => setInventoryLocationFilter('all')} sx={{ fontWeight: 800, textTransform: 'none' }}>
            Show all
          </Button>
        )}
      </Paper>

      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 3, overflowX: 'auto' }}>
        <Table sx={{ minWidth: 1040 }}>
          <TableHead>
            <TableRow sx={{ background: 'linear-gradient(135deg, #001f3f 0%, #003d7a 100%)' }}>
              <TableCell sx={{ color: 'white' }} />
              <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: 14 }}>SKU</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: 14 }}>Part Type</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: 14 }}>OEM Part #</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: 14 }}>Serial #</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: 14 }}>Location</TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', fontSize: 14 }}>Qty</TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', fontSize: 14 }}>Condition</TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', fontSize: 14 }}>Status</TableCell>
              <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold', fontSize: 14 }}>Value</TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', fontSize: 14 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 6, color: 'text.secondary', fontWeight: 800 }}>
                  No parts found for the selected inventory location.
                </TableCell>
              </TableRow>
            )}
            {paginatedRows.map((part) => (
              <PartRow
                key={part.id}
                part={part}
                onEdit={handleEditPart}
                onDelete={handleDeletePart}
                getTruckName={getTruckName}
                truckById={truckById}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component={Paper}
        count={filteredRows.length}
        page={page}
        onPageChange={(_event, nextPage) => setPage(nextPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[25, 50, 100, 150]}
        sx={{ mt: 1, borderRadius: 2 }}
      />

      <AddPartDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onPartAdded={handlePartAdded}
      />

      <EditPartDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        part={selectedPart}
        onPartUpdated={handlePartUpdated}
      />
    </Box>
  );
}
